import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import ExternalUserScreen from './ExternalUserScreen';
import UserSelector from './UserSelector';
import { getAuthHeader } from '../utils/tokenManager';

const apiUrl = process.env.REACT_APP_DJANGO_API_URL;
const webUrl = process.env.REACT_APP_WEB_URL;

const SharedScreen = () => {
    const { uuid } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [userData, setUserData] = useState({
        selectedPayments: {},
        selectedContactInfo: {},
        selectedSocialMedia: {},
        selectedProducts: []
    });
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const checkAccountStatus = async () => {
            try {
                // For public routes, we don't need authentication headers
                const response = await fetch(`${apiUrl}/api/${uuid}/is_active/`);
                const data = await response.json();
                
                // If we get a successful response but account is not active
                if (response.ok && !data.is_active) {
                    navigate('/link_account', { state: { account_id: uuid } });
                    return false;
                }
                
                // If we get any kind of error response (404, 500, etc)
                if (!response.ok) {
                    navigate('/link_account', { state: { account_id: uuid } });
                    return false;
                }
                
                return true;
            } catch (error) {
                console.error('Error checking account status:', error);
                navigate('/link_account', { state: { account_id: uuid } });
                return false;
            }
        };

        const fetchUserData = async () => {
            try {
                const isActive = await checkAccountStatus();
                if (!isActive) {
                    setLoading(false);
                    return;
                }

                const user_id = searchParams.get('user_id');
                const url = user_id 
                    ? `${apiUrl}/api/${uuid}/get_user_data/?user_id=${user_id}`
                    : `${apiUrl}/api/${uuid}/get_user_data/`;

                // For public routes, we don't need authentication headers
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                const data = await response.json();
                setUsers(data.users);

                // If there's only one user, set up the data for ExternalUserScreen
                if (data.users.length === 1) {
                    const user = data.users[0];
                    const payments_data = user.payments || {};
                    const contact_data = user.contact || {};
                    const social_data = user.social || {};
                    const userDisplay = user.display_name || '';
                    const actual_uuid = user.account_id || uuid;
                    const displayed_products = user.displayed_products || [];

                    for (const j of Object.keys(social_data)) {
                        social_data[j] = { id: j, showInput: social_data[j] ? true : false, value: social_data[j] }
                    }
                    for (const j of Object.keys(contact_data)) {
                        contact_data[j] = { id: j, showInput: contact_data[j] ? true : false, value: contact_data[j] }
                    }
                    for (const j of Object.keys(payments_data)) {
                        payments_data[j] = { id: j, showInput: payments_data[j] ? true : false, value: payments_data[j] }
                    }

                    setUserData({
                        selectedPayments: payments_data,
                        selectedContactInfo: contact_data,
                        selectedSocialMedia: social_data,
                        userDisplay: userDisplay,
                        actual_uuid: actual_uuid,
                        selectedProducts: displayed_products
                    });
                }
            } catch (error) {
                console.error("Error fetching user data:", error);
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, [uuid, searchParams, navigate]);

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error loading data</div>;

    // Show UserSelector if there are multiple users and no specific user_id
    if (users.length > 1 && !searchParams.get('user_id')) {
        return <UserSelector users={users} account_id={uuid} />;
    }

    // Show ExternalUserScreen for single user or when specific user_id is selected
    return (
        <ExternalUserScreen
            selectedPayments={userData.selectedPayments}
            selectedContactInfo={userData.selectedContactInfo}
            selectedSocialMedia={userData.selectedSocialMedia}
            userDisplay={userData.userDisplay}
            actual_uuid={userData.actual_uuid}
            selectedProducts={userData.selectedProducts}
        />
    );
};

export default SharedScreen;
